import type { PricingChange } from '../pricingRecap'
import type { VendorFeeFormRow } from './VendorFeesForm.interface'

/**
 * Ordered as the table's columns are, so the recap reads in the same order the admin edited.
 * `id` is deliberately absent — it is a client-side React key, never a pricing field.
 */
const ROW_FIELDS: Array<{ field: Exclude<keyof VendorFeeFormRow, 'id'>; label: string }> = [
  { field: 'fee', label: 'Fee (cents)' },
  { field: 'rate', label: 'Rate (%)' },
  { field: 'feeCapCents', label: 'Max Fee (cents)' },
  { field: 'type', label: 'Type' },
  { field: 'title', label: 'Title' },
  { field: 'applicableDestinations', label: 'Destination Type' },
  { field: 'contractorType', label: 'Contractor Type' },
]

const MISSING_VALUE = '—'

function toComparable(value: string | string[]): string {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return MISSING_VALUE
    }

    return value.join(', ')
  }

  const trimmed = value.trim()

  if (trimmed === '') {
    return MISSING_VALUE
  }

  return trimmed
}

function describeRow(row: VendorFeeFormRow, index: number): string {
  const title = row.title.trim()

  if (title === '') {
    return `Row ${index + 1}`
  }

  return title
}

/**
 * Diffs the form rows BY `id`, which is the whole point of this module.
 *
 * The obvious alternative — diffing the mapped payloads by array position — is wrong, and was
 * written and reverted once already: `toSavePayload` filters rows, so removing a non-last row
 * shifts every later index and the diff reports a change to a row nobody touched (delete B from
 * [A, B, C] and position 1 compares B against C). Row ids survive removals, so matching on them
 * reports exactly what moved.
 *
 * Under-reporting is safer than over-reporting on a live-pricing confirmation, so a row whose
 * fields are all unchanged contributes nothing at all.
 */
export function buildVendorFeesRecap(
  before: VendorFeeFormRow[],
  after: VendorFeeFormRow[],
): PricingChange[] {
  const beforeById = new Map(
    before.map((row) => {
      return [row.id, row] as const
    }),
  )
  const afterIds = new Set(
    after.map((row) => {
      return row.id
    }),
  )

  const removals = before.reduce<PricingChange[]>((changes, row, index) => {
    if (afterIds.has(row.id)) {
      return changes
    }

    changes.push({ label: describeRow(row, index), from: 'Fee line', to: 'Removed' })

    return changes
  }, [])

  const additionsAndEdits = after.reduce<PricingChange[]>((changes, row, index) => {
    const previous = beforeById.get(row.id)

    if (previous === undefined) {
      changes.push({ label: describeRow(row, index), from: MISSING_VALUE, to: 'Added' })

      return changes
    }

    ROW_FIELDS.forEach(({ field, label }) => {
      const from = toComparable(previous[field])
      const to = toComparable(row[field])

      if (from === to) {
        return
      }

      changes.push({ label: `${describeRow(row, index)} — ${label}`, from, to })
    })

    return changes
  }, [])

  return [...removals, ...additionsAndEdits]
}
